import { Box, Typography } from "@mui/material";
import type { Theme } from "@mui/material/styles";
import React from "react";

import { ClaimPart, Stance } from "../../types/productPlane";
import EntityLink from "./EntityLink";
import Tag from "./Tag";

// The cells every table of capability claims draws the same way: the claim
// table of a bearer and the bearer table of a capability part.

// The stances that state a gap; they are set apart in the warning colour
const GAP_STANCES: ReadonlySet<Stance> = new Set<Stance>(["requires", "lacks"]);

export const quiet = { color: "text.secondary" } as const;

// The head of a column: small capitals in the quieter colour
export const columnHeader = {
  ...quiet,
  fontSize: "0.72rem",
  fontWeight: 500,
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  whiteSpace: "nowrap",
} as const;

// The box every table of a detail view stands in: a hairline round it, the
// corners of a surface, on the raised surface
export const tableFrame = {
  border: 1,
  borderColor: "divider",
  borderRadius: (theme: Theme) => `${theme.radius.surface}px`,
  backgroundColor: "background.raised",
  overflow: "hidden",
} as const;

// An object beneath its ability: a grey caption, a link or not
const quieterPart = { ...quiet, fontSize: "0.75rem" } as const;

// What a bearer is said to do with the capability: a quiet tag, a gap in
// the warning colour
export const StanceChip: React.FC<{ stance: Stance }> = ({ stance }) => (
  <Tag tone={GAP_STANCES.has(stance) ? "warning" : "default"}>{stance}</Tag>
);

export interface ClaimPartNameProps {
  part: ClaimPart;
  // Smaller and in the quieter colour, a link included: an object beneath
  // its ability
  quieter?: boolean;
  // True for the part the page around the table is about: it is set apart
  // and is not a link to itself
  own?: boolean;
}

// One part of a claim: a link when it is on a curated list, else the
// article's own words, set apart
export const ClaimPartName: React.FC<ClaimPartNameProps> = ({
  part,
  quieter = false,
  own = false,
}) =>
  part.curated && !own ? (
    <EntityLink
      id={part.part.id}
      type={part.part.type}
      name={part.part.name}
      sx={
        quieter
          ? { ...quieterPart, textDecorationColor: "currentColor" }
          : undefined
      }
    />
  ) : (
    <Box
      component="span"
      title={part.curated ? undefined : "In the article's own words"}
      aria-current={own ? "true" : undefined}
      sx={{
        ...(part.curated
          ? {}
          : {
              fontStyle: "italic",
              textDecoration: "underline dotted",
              textUnderlineOffset: "3px",
            }),
        ...(quieter ? quieterPart : {}),
        ...(own ? { color: "text.primary", fontWeight: 600 } : {}),
      }}
    >
      {part.part.name}
    </Box>
  );

export interface ClaimPartsProps {
  parts: ClaimPart[];
  quieter?: boolean;
  // The id of the part the page around the table is about, where it is one
  ownId?: string;
}

// The parts of one role, one beneath the other
export const ClaimParts: React.FC<ClaimPartsProps> = ({
  parts,
  quieter,
  ownId,
}) => (
  <>
    {parts.map((part) => (
      <Box key={part.part.id}>
        <ClaimPartName
          part={part}
          quieter={quieter}
          own={ownId !== undefined && part.part.id === ownId}
        />
      </Box>
    ))}
  </>
);

// A line beneath what a cell states, in the quieter colour
export const Beneath: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <Typography variant="caption" component="div" sx={quiet}>
    {children}
  </Typography>
);

// What a cell with nothing to state shows
export const NONE = (
  <Box component="span" aria-hidden sx={{ color: "text.disabled" }}>
    —
  </Box>
);
